import { useCallback, useEffect, useRef, useState } from 'react';
import type { ExifAnalysis } from '@xphoto/shared';
import { ApiError } from '../gallery/api';
import { useJobActivity } from '../notifications/use-job-activity';
import { hasActiveJobOfType } from '../notifications/job-activity';
import { fetchExifAnalysis } from './exif-api';

/**
 * Charge le rapport d'analyse EXIF d'une bibliothèque (T4b, XPHOTO-24). Mirroir
 * de `useAssetDetail` : `AbortController`, états idle/loading/error/ready.
 *
 * `libraryId === ''` ⇒ inerte (aucune requête). Le `refreshKey` force un
 * rechargement (p. ex. après avoir enfilé un job de publication/effacement).
 *
 * `folder` (optionnel) : borne l'analyse au sous-arbre (préfixe `rel_path`, même
 * sémantique que la portée d'effacement). Un changement de `folder` RECHARGE
 * l'analyse → les cartes ne reflètent que le dossier choisi (Correctif portée).
 *
 * AUTO-RAFRAÎCHISSEMENT (Correctif 3) : le job `exif_erase` resynchronise déjà
 * l'`exif_snapshot` des fichiers traités « vérité disque » APRÈS écriture (aucun
 * rescan de toute la bibliothèque nécessaire). Ce hook observe l'activité des jobs
 * (cloche) et, à la TRANSITION actif→plus-actif d'un job `exif_erase`, RECHARGE
 * l'analyse tout seul → la page reflète la réalité sans action manuelle.
 */

export type AnalysisStatus = 'idle' | 'loading' | 'error' | 'ready';

export interface UseExifAnalysisResult {
  status: AnalysisStatus;
  data: ExifAnalysis | null;
  errorCode: string | null;
  reload: () => void;
}

export function useExifAnalysis(
  libraryId: string,
  refreshKey = 0,
  folder?: string,
): UseExifAnalysisResult {
  const [status, setStatus] = useState<AnalysisStatus>('idle');
  const [data, setData] = useState<ExifAnalysis | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  // Bumpé par `reload()` pour rejouer l'effet sans changer `libraryId`.
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!libraryId) {
      controllerRef.current?.abort();
      setStatus('idle');
      setData(null);
      setErrorCode(null);
      return;
    }
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setStatus('loading');
    setErrorCode(null);

    fetchExifAnalysis(libraryId, folder, controller.signal)
      .then((d) => {
        if (controller.signal.aborted) return;
        setData(d);
        setStatus('ready');
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setErrorCode(err instanceof ApiError ? err.code : 'NETWORK');
        setStatus('error');
      });

    return () => controller.abort();
  }, [libraryId, refreshKey, reloadKey, folder]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  // --- Auto-rafraîchissement à la fin d'un job `exif_erase` -------------------
  // Le resync « vérité disque » du job rend l'`exif_snapshot` à jour ; il ne manque
  // qu'à recharger la vue affichée. On détecte le FRONT DESCENDANT (un `exif_erase`
  // était actif, ne l'est plus) et on recharge alors — pas à chaque tick.
  const activity = useJobActivity(libraryId);
  const eraseActive = hasActiveJobOfType(activity, 'exif_erase');
  const prevEraseActiveRef = useRef(eraseActive);
  useEffect(() => {
    if (prevEraseActiveRef.current && !eraseActive) {
      // Un effacement vient de se terminer → recharge l'analyse.
      setReloadKey((k) => k + 1);
    }
    prevEraseActiveRef.current = eraseActive;
  }, [eraseActive]);

  return { status, data, errorCode, reload };
}
